/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useMemo } from "react";
import { usePriceFeed } from "../context/PriceFeedContext";
import { getIndicatorKey } from "../utils/indicatorUtils";
import { calcStopLoss, calcTakeProfit, calcTradeValues, calcMultiValues } from "../utils/calculations";
import { roundToStepString } from "../utils/lotUtils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const RiskManagement = ({
  tradeDirection,
  askPrice,
  bidPrice,
  pipSize,
  contractSize,
  accountEquity,
  freeMargin,
  cashAvailable,
  accountCurrency,
  symbolCurrency,
  priceConversionRate,
  lotStep,
  minLot,
  maxLot,
  setLotSize,
  setStopLossPrice,
  setStopLossDistance,
  setTakeProfitValue,
  takeProfitValue,
  setTakeProfitPrice,
  takeProfitPrice,
  rrRatio,
  setRrRatio,
  setProjectedLoss,
  setProjectedProfit,
  selectedSymbol,
  selectedTimeframe,
  riskPercent,
  setRiskPercent,
  profitMode,
  setProfitMode,
  targets,
  onTargetsCalculated,
  orderType,
  limitEntryPrice,
}) => {
  const [slMode, setSlMode] = useState("distance");
  const [stopLossValue, setStopLossValue] = useState("");
  const [tpMode, setTpMode] = useState("rr_ratio");
  const [atrMultiplier, setAtrMultiplier] = useState(1.5);
  const [atrValue, setAtrValue] = useState(null);

  const { sendIndicatorAction, activeIndicators } = usePriceFeed();

  const atrIndicatorKey = useMemo(() => {
    if (selectedSymbol && selectedTimeframe) {
      return getIndicatorKey("atr", { length: 14 }, selectedSymbol, selectedTimeframe, "pane");
    }
    return null;
  }, [selectedSymbol, selectedTimeframe]);

  // Effect to subscribe/unsubscribe from ATR indicator
  useEffect(() => {
    if (slMode === "atr" && atrIndicatorKey) {
      const atrIndicator = {
        name: "atr",
        params: { length: 14 },
        unique_id: atrIndicatorKey,
        pane_type: "pane",
        scale_type: "regular"
      };
      sendIndicatorAction('add_indicator', {
        indicator: atrIndicator.name,
        params: atrIndicator.params,
        unique_id: atrIndicator.unique_id,
  count: 1000,
        pane_type: atrIndicator.pane_type,
        scale_type: atrIndicator.scale_type
      });

      // Cleanup: remove the ATR indicator that we added. Use the known unique id so
      // switching timeframe or changing SL mode unmounts the same indicator instance.
      const addedUid = atrIndicator.unique_id;
      return () => {
        try {
          if (addedUid) {
            sendIndicatorAction('remove_indicator', { unique_id: addedUid });
          }
        } catch {
          // best-effort: swallow errors
        }
      };
    }
  }, [slMode, atrIndicatorKey, sendIndicatorAction]);

  // Effect to process the received ATR value
  useEffect(() => {
    if (slMode === 'atr') {
      const atrIndicator = activeIndicators.find(ind => ind.id === atrIndicatorKey);
      if (atrIndicator && atrIndicator.data) {
        const indData = atrIndicator.data;
        // Determine the data-key name the backend used (e.g. "atr_atr")
        const dataKey = Array.isArray(atrIndicator.data_keys) && atrIndicator.data_keys.length > 0
          ? atrIndicator.data_keys[0]
          : 'atr_atr';

        // Object-of-arrays format: { atr_atr: [{time, value}, ...] }
        if (!Array.isArray(indData) && typeof indData === 'object' && indData !== null) {
          const series = indData[dataKey];
          if (Array.isArray(series) && series.length > 0) {
            const last = series[series.length - 1];
            const v = last?.value;
            if (typeof v === 'number' && Number.isFinite(v)) {
              setAtrValue(v);
              return;
            }
          }
        }

        // Array-of-points format: [{time, atr_atr, ...}, ...]
        // After indicator_update messages, latest points use {time, value} instead of {time, atr_atr}.
        // Scan from the end to find the first point with a usable value.
        if (Array.isArray(indData)) {
          for (let i = indData.length - 1; i >= 0; i--) {
            const pt = indData[i];
            if (!pt) continue;
            const v = pt[dataKey] ?? pt.value;
            if (typeof v === 'number' && Number.isFinite(v)) {
              setAtrValue(v);
              return;
            }
          }
        }
      }
    }
  }, [activeIndicators, slMode, atrIndicatorKey]);


  useEffect(() => {
    let slDistance, slPrice, tpDistance, tpPrice;
    const entryPrice = orderType === 'LIMIT' && limitEntryPrice ? parseFloat(limitEntryPrice) : (tradeDirection === "BUY" ? askPrice : bidPrice);

    if (!entryPrice || isNaN(entryPrice)) {
      if (slMode === 'price') {
        const calculated = calcStopLoss({
          stopLossMode: 'price',
          stopLossValue,
          tradeDirection,
          pipSize,
        });
        setStopLossDistance(calculated.slDistance);
        setStopLossPrice(calculated.slPrice);
      }
      if (tpMode === 'price') {
        const calculated = calcTakeProfit({
          takeProfitMode: 'price',
          takeProfitValue: takeProfitPrice,
          tradeDirection,
          pipSize,
        });
        setTakeProfitValue(calculated.tpDistance);
        setTakeProfitPrice(calculated.tpPrice);
      }
      return;
    }

    // Stop Loss Calculation
    if (slMode === 'atr' && atrValue) {
      const { slDistance: atrSlDistance, slPrice: atrSlPrice } = calcStopLoss({
        stopLossMode: 'atr',
        stopLossValue: atrMultiplier * atrValue,
        tradeDirection,
        askPrice,
        bidPrice,
        pipSize,
        entryPrice,
      });
      slDistance = atrSlDistance;
      slPrice = atrSlPrice;
    } else {
      const { slDistance: manualSlDistance, slPrice: manualSlPrice } = calcStopLoss({
        stopLossMode: slMode,
        stopLossValue: stopLossValue,
        tradeDirection,
        askPrice,
        bidPrice,
        pipSize,
        entryPrice,
      });
      slDistance = manualSlDistance;
      slPrice = manualSlPrice;
    }

    setStopLossDistance(slDistance);
    setStopLossPrice(slPrice);

    // Take Profit Calculation
    if (tpMode === 'price') {
        const calculated = calcTakeProfit({
          takeProfitMode: 'price',
          takeProfitValue: takeProfitPrice,
          tradeDirection,
          askPrice,
          bidPrice,
          pipSize,
          entryPrice,
        });
        tpDistance = calculated.tpDistance;
        tpPrice = calculated.tpPrice;
    } else {
        const { tpDistance: calculatedTpDistance, tpPrice: calculatedTpPrice } = calcTakeProfit({
            takeProfitMode: tpMode,
            takeProfitValue: tpMode === 'rr_ratio' ? rrRatio : takeProfitValue,
            stopLossDistance: slDistance,
            tradeDirection,
            askPrice,
            bidPrice,
            pipSize,
            entryPrice,
        });
        tpDistance = calculatedTpDistance;
        tpPrice = calculatedTpPrice;
    }

    setTakeProfitValue(tpDistance);
    setTakeProfitPrice(tpPrice);
    
    if (tpMode === 'distance') {
        const { tpDistance: calculatedTpDistance, tpPrice: calculatedTpPrice } = calcTakeProfit({
            takeProfitMode: 'distance',
            takeProfitValue: takeProfitValue,
            stopLossDistance: slDistance,
            tradeDirection,
            askPrice,
            bidPrice,
            pipSize,
            entryPrice,
        });
        setTakeProfitValue(calculatedTpDistance);
        setTakeProfitPrice(calculatedTpPrice);
    }

    // Lot Size, Projected P/L
    if (profitMode === 'multi') {
      const { lotSize, projectedLoss, projectedProfit, weightedRR, updatedTargets } = calcMultiValues({
        accountEquity,
        riskPercent,
        slDistance,
        targets,
        pipSize,
        contractSize,
        askPrice,
        bidPrice,
        tradeDirection,
        atrValue,
        entryPrice,
        freeMargin,
        cashAvailable,
        accountCurrency,
        symbolCurrency,
        priceConversionRate,
      });
  // Round lot size to broker step for display
  setLotSize(roundToStepString(parseFloat(lotSize), lotStep, 'nearest', minLot, maxLot));
      setProjectedLoss(parseFloat(projectedLoss));
      setProjectedProfit(parseFloat(projectedProfit));
      setRrRatio(weightedRR.toFixed(2));
      if (onTargetsCalculated) {
        onTargetsCalculated(updatedTargets);
      }
    } else {
      const { lotSize, projectedLoss, projectedProfit } = calcTradeValues({
        accountEquity,
        riskPercent,
        slDistance,
        tpDistance,
        pipSize,
        contractSize,
        freeMargin,
        cashAvailable,
        accountCurrency,
        symbolCurrency,
        priceConversionRate,
        entryPrice,
      });
  setLotSize(roundToStepString(parseFloat(lotSize), lotStep, 'nearest', minLot, maxLot));
      setProjectedLoss(parseFloat(projectedLoss));
      setProjectedProfit(parseFloat(projectedProfit));
    }
  }, [
    slMode, stopLossValue, tpMode, takeProfitValue, takeProfitPrice, rrRatio, riskPercent, atrMultiplier, atrValue,
    tradeDirection, askPrice, bidPrice, pipSize, contractSize, accountEquity, freeMargin, cashAvailable,
    accountCurrency, symbolCurrency, priceConversionRate, selectedSymbol, selectedTimeframe,
    profitMode, targets, onTargetsCalculated, setLotSize, setStopLossPrice, setStopLossDistance,
    setTakeProfitValue, setTakeProfitPrice, setRrRatio, setProjectedLoss, setProjectedProfit,
    orderType, limitEntryPrice, lotStep, minLot, maxLot
  ]);

  const fieldLabelClassName = 'trade-panel-field-label block mb-1 font-medium text-textSecondary';
  const modeButtonClassName = 'trade-panel-toggle-button';
  const compactMetricGridStyle = { gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.82fr) minmax(0, 1fr) minmax(0, 0.82fr)' };
  const compactMetricInputStyle = {
    paddingLeft: '0.6rem',
    paddingRight: '0.6rem',
    paddingTop: '0.45rem',
    paddingBottom: '0.45rem',
    fontSize: '0.95rem',
  };
  const stopLossFieldLabel = slMode === 'atr'
    ? 'ATR Multiplier'
    : slMode === 'distance'
      ? 'SL (pips)'
      : 'SL Price';

  return (
    <div className="space-y-3">
      {/* Profit taking mode toggle */}
      <div>
        <label className={fieldLabelClassName}>Profit Taking</label>
        <div className="flex flex-wrap gap-1">
          <Button
            variant={profitMode === 'single' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setProfitMode && setProfitMode('single')}
            className={modeButtonClassName}
          >
            Single TP
          </Button>
          <Button
            variant={profitMode === 'multi' ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setProfitMode && setProfitMode('multi')}
            className={modeButtonClassName}
          >
            Multi-step
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className={fieldLabelClassName}>Stop Loss Mode</label>
          <div className="flex flex-wrap gap-1">
            <Button
              variant={slMode === "distance" ? "primary" : "outline"}
              size="sm"
              onClick={() => setSlMode("distance")}
              className={modeButtonClassName}
            >
              Distance
            </Button>
            <Button
              variant={slMode === "price" ? "primary" : "outline"}
              size="sm"
              onClick={() => setSlMode("price")}
              className={modeButtonClassName}
            >
              Price
            </Button>
            <Button
              variant={slMode === "atr" ? "primary" : "outline"}
              size="sm"
              onClick={() => setSlMode("atr")}
              className={modeButtonClassName}
            >
              ATR R/R
            </Button>
          </div>
        </div>
        <div>
          <label className={fieldLabelClassName}>Take Profit Mode</label>
          <div className="flex flex-wrap gap-1">
            <Button
              variant={tpMode === "rr_ratio" ? "primary" : "outline"}
              size="sm"
              onClick={() => setTpMode("rr_ratio")}
              className={modeButtonClassName}
            >
              R/R
            </Button>
            <Button
              variant={tpMode === "distance" ? "primary" : "outline"}
              size="sm"
              onClick={() => setTpMode("distance")}
              className={modeButtonClassName}
            >
              Distance
            </Button>
            <Button
              variant={tpMode === "price" ? "primary" : "outline"}
              size="sm"
              onClick={() => setTpMode("price")}
              className={modeButtonClassName}
            >
              Price
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-1.5" style={compactMetricGridStyle}>
        <div className="min-w-0">
          <label className={fieldLabelClassName}>{stopLossFieldLabel}</label>
          <Input
            type="number"
            value={slMode === 'atr' ? atrMultiplier : stopLossValue}
            onChange={(e) => {
              if (slMode === 'atr') {
                setAtrMultiplier(parseFloat(e.target.value) || 1);
                return;
              }
              setStopLossValue(e.target.value);
            }}
            className="font-mono"
            style={compactMetricInputStyle}
          />
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClassName}>R/R</label>
          <Input
            type="number"
            value={rrRatio}
            onChange={(e) => setRrRatio(e.target.value)}
            className="font-mono"
            style={compactMetricInputStyle}
          />
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClassName}>Take Profit</label>
          <Input
            type="number"
            value={tpMode === "price" ? takeProfitPrice : takeProfitValue}
            onChange={(e) => {
              const val = e.target.value;
              if (tpMode === "price") {
                setTakeProfitPrice(val);
              } else {
                setTakeProfitValue(val);
              }
            }}
            className="font-mono"
            style={compactMetricInputStyle}
          />
        </div>
        <div className="min-w-0">
          <label className={fieldLabelClassName}>Risk %</label>
          <Input
            type="number"
            value={riskPercent}
            onChange={(e) => setRiskPercent(parseFloat(e.target.value))}
            className="font-mono"
            style={compactMetricInputStyle}
          />
        </div>
      </div>
      {slMode === "atr" && (
        <p className="trade-panel-field-label mt-1 text-textSecondary">
          ATR Value: <span className="font-mono text-textPrimary">{atrValue ? atrValue.toFixed(5) : "Loading..."}</span>
        </p>
      )}
    </div>
  );
};

export default RiskManagement;
